import * as yup from 'yup';
import { FieldValueTypes, type AddonProductType, type FormField } from '@dtp/membership-service-types';
import type { AddonsFormType } from '@appTypes/addonsFormType';
import type { MembersFormType } from '@appTypes/membersFormType';

export const householdMemberAmount = 2;
export const householdMemberIndex = 0;

export type AddonsFormValues = AddonsFormType & {
  otherMembers?: MembersFormType['otherMembers'];
};

export type AddonFormMember = Partial<MembersFormType['mainMember']> | undefined;

const isAddonProductFormValue = (productData: unknown): productData is AddonsFormType[string] =>
  !!productData &&
  typeof productData === 'object' &&
  !Array.isArray(productData) &&
  Array.isArray((productData as AddonsFormType[string]).members);

export const getAddonsData = (data: AddonsFormValues, memberCount: number): AddonsFormType =>
  Object.entries(data as Record<string, unknown>).reduce((acc: AddonsFormType, [productId, productData]) => {
    if (productId === 'otherMembers' || !isAddonProductFormValue(productData)) return acc;

    acc[productId] = {
      ...productData,
      members: productData.members.filter((memberIndex) => Number(memberIndex) < memberCount),
    };
    return acc;
  }, {});

const getFieldSchema = (field: FormField) => {
  const validation = field.validation || {};
  let schema: yup.StringSchema | yup.NumberSchema | yup.AnySchema =
    field.type === FieldValueTypes.Number ? yup.number() : yup.string();

  if (validation.required?.value) {
    schema = schema.required(validation.required.message);
  }
  if (validation.minLength?.value && 'min' in schema) {
    schema = (schema as yup.StringSchema).min(validation.minLength.value, validation.minLength.message);
  }
  if (validation.maxLength?.value && 'max' in schema) {
    schema = (schema as yup.StringSchema).max(validation.maxLength.value, validation.maxLength.message);
  }
  if (validation.pattern?.value && 'matches' in schema) {
    schema = (schema as yup.StringSchema).matches(validation.pattern.value, validation.pattern.message);
  }
  if (validation.min?.value && field.type === FieldValueTypes.Number) {
    schema = (schema as yup.NumberSchema).min(validation.min.value, validation.min.message);
  }
  if (validation.max?.value && field.type === FieldValueTypes.Number) {
    schema = (schema as yup.NumberSchema).max(validation.max.value, validation.max.message);
  }

  return schema;
};

export const getAddonProductSchema = (product: AddonProductType) => {
  const propsShape = product.fieldComponents.reduce((acc: Record<string, yup.AnySchema>, field) => {
    acc[field.id] = getFieldSchema(field);
    return acc;
  }, {});

  return yup.object().shape({
    members: yup.array().of(yup.string()),
    props: yup.object().when('members', {
      is: (members?: string[]) => !!members?.length,
      then: yup.object().shape(propsShape),
      otherwise: yup.object().notRequired(),
    }),
  });
};
